/**
 * Paginated list of the actions recorded for one store copy attempt, filtered by action category
 * on the server. One instance per expanded attempt panel.
 */
// eslint-disable-next-line
import { action, makeObservable, observable, runInAction } from 'mobx';

import { rootContainer, TYPE } from '../../../../../services/container/root';
import PagedDataStore, { IFetchListArgs, IPagedData } from '../../../../../stores/abstract-paged-data-store';
import TroubleshootingService, {
    IListEnvelope,
    IStoreCopyAction,
} from '../../../../../services/api/troubleshooting-service';
import { CATEGORY_ACTIONS } from '../action-categories';
import type { ActionCategory } from '../action-categories';

export class AttemptActionsPagedListStore extends PagedDataStore<IStoreCopyAction> {
    readonly perPage: number = 50;

    @observable attemptId: number | null = null;
    @observable category: ActionCategory = 'all';
    @observable error: string | null = null;

    constructor() {
        super();
        makeObservable(this);
    }

    private get troubleshootingService(): TroubleshootingService {
        return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
    }

    @action
    async open(attemptId: number, category: ActionCategory): Promise<void> {
        this.attemptId = attemptId;
        this.category = category;
        this.error = null;
        await this.loadPage(1);
    }

    @action
    async setCategory(category: ActionCategory): Promise<void> {
        this.category = category;
        await this.loadPage(1);
    }

    private mapActionsPageToPagedData(page: IListEnvelope<IStoreCopyAction>): IPagedData<IStoreCopyAction> {
        const { per_page: perPage, current_page: currentPage, total } = page.pagination;
        const lastPage = page.pagination.last_page ?? Math.max(1, Math.ceil(total / perPage));
        const from = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
        const to = Math.min(currentPage * perPage, total);

        return {
            data: page.data,
            current_page: currentPage,
            per_page: perPage,
            total,
            last_page: lastPage,
            from,
            to,
            path: '',
            next_page_url: '',
        };
    }

    protected async fetchList(args: IFetchListArgs): Promise<IPagedData<IStoreCopyAction> | undefined> {
        if (this.attemptId == null) {
            return undefined;
        }

        const { attemptId, category } = this;
        try {
            const page = await this.troubleshootingService.getStoreCopyAttemptActions(attemptId, {
                actions: CATEGORY_ACTIONS[category] ?? undefined,
                page: args.page,
                perPage: args.perPage,
            });
            // Drop a response that a quicker tab switch has already superseded.
            if (attemptId !== this.attemptId || category !== this.category) {
                return undefined;
            }
            runInAction(() => {
                this.error = null;
            });
            return this.mapActionsPageToPagedData(page);
        } catch (err: any) {
            runInAction(() => {
                this.error = err?.response?.data?.error ?? err?.message ?? 'Failed to load actions';
            });
            return undefined;
        }
    }
}
